"use client";

import { useEffect, useRef } from "react";
import {
  trackRecommendation,
  trackDwellTime,
  getAndClearNavigationSource,
  type InteractionSource,
} from "@/lib/recommendation-tracker";

/**
 * Recommendation tracking for the event page. Captures:
 *  - The navigation source (where the user came from), read once on
 *    mount via `getAndClearNavigationSource` so we don't double-count
 *    if the user re-enters the page later.
 *  - Dwell time, flushed on tab hide and on React unmount (SPA nav).
 *  - A single `view_detail` recommendation event per slug, fired once
 *    `eventData` arrives.
 *
 * `eventData` is intentionally typed as `unknown` here — the hook only
 * needs to know "data exists" to fire the view event, not its shape.
 */
export function useDwellTracking(slug: string, eventData: unknown) {
  const navSourceRef = useRef<InteractionSource | null>(null);
  const trackedSlugRef = useRef<string | null>(null);
  const dwellStartRef = useRef<number>(Date.now());

  useEffect(() => {
    navSourceRef.current = getAndClearNavigationSource();
    dwellStartRef.current = Date.now();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && slug) {
        const duration = Date.now() - dwellStartRef.current;
        trackDwellTime(slug, duration, navSourceRef.current || "direct");
      } else {
        dwellStartRef.current = Date.now();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (slug) {
        const duration = Date.now() - dwellStartRef.current;
        trackDwellTime(slug, duration, navSourceRef.current || "direct");
      }
    };
  }, [slug]);

  useEffect(() => {
    if (eventData && trackedSlugRef.current !== slug) {
      trackedSlugRef.current = slug;
      const navSource = navSourceRef.current || "direct";
      trackRecommendation(slug, "view_detail", navSource);
    }
  }, [eventData, slug]);
}
